/* eslint-disable react/prop-types */

/**
 * AuctionInformation — Displays auctioneer details, auction firm details,
 * and property listing metadata in a three-column card layout with a green
 * top border accent. Renders between the Disclaimers section and the FAQ
 * section in CommercialPDP.
 */


/**
 * Converts a millisecond timestamp to formatted date string (M/D/YYYY).
 */
const formatTimestamp = (ms) => {
  if (!ms) return '';
  const date = new Date(ms);
  if (isNaN(date.getTime())) return '';
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

/**
 * Returns true if value is non-null, non-undefined, non-empty, and not "-" after trimming.
 * Matches the pattern used by ListedBy.jsx in the other PDP category.
 * Handles: null → false, undefined → false, "" → false, "  " → false, "-" → false, "valid" → true
 */
const hasContent = (value) => {
  if (value === null || value === undefined) return false;
  const trimmed = String(value).trim();
  return trimmed.length > 0 && trimmed !== '-';
};

/**
 * PhoneIcon — Inline SVG phone icon displayed next to phone number links.
 */
const PhoneIcon = () => (
  <svg
    className="commercial-pdp__auction-phone-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AuctionInformation = ({ pdpInitData }) => {
  // Data extraction using optional chaining
  const listingAgentDetail = pdpInitData?.listingAgentDetail;
  const auctioneerDetail = listingAgentDetail?.auctioneerDetail || pdpInitData?.auctioneerDetail?.auctioneerDetail;
  const auctionFirmDetail = listingAgentDetail?.auctionFirmDetail || pdpInitData?.auctioneerDetail?.auctionFirmDetail;
  const listingDetail = pdpInitData?.listingDetail;
  const propertyDetails = pdpInitData?.propertyDetails;
  const coListingAgentDetail = pdpInitData?.coListingAgentDetail;

  // RHSS condition — matches ListedBy.jsx in other PDP category
  const isRhss = listingAgentDetail?.listingAgentEmailId?.includes("@rhss.com");

  // Check if at least one of auctioneerDetail, auctionFirmDetail, or listingAgentDetail has content
  const hasAuctioneerContent = auctioneerDetail
    ? Object.values(auctioneerDetail).some((val) => hasContent(val))
    : false;

  const hasFirmContent = auctionFirmDetail
    ? Object.values(auctionFirmDetail).some((val) => hasContent(val))
    : false;

  const hasListingAgent = listingAgentDetail && (
    hasContent(listingAgentDetail.listingAgentFirstName) ||
    hasContent(listingAgentDetail.listingAgentLastName) ||
    hasContent(listingAgentDetail.listingAgentName) ||
    hasContent(listingAgentDetail.agency) ||
    hasContent(listingAgentDetail.listingAgentLicenseNumber) ||
    hasContent(listingAgentDetail.listingAgentPhone)
  );

  // If nothing has content, don't render
  if (!hasAuctioneerContent && !hasFirmContent && !hasListingAgent) return null;

  // "About the Property" column visibility
  const hasPropertyContent =
    hasContent(propertyDetails?.propertyCreatedDate) ||
    hasContent(propertyDetails?.propertyLastUpdated) ||
    hasContent(propertyDetails?.listingId) ||
    hasContent(listingDetail?.dateFormattedListingStartDate) ||
    hasContent(listingDetail?.dateFormattedListingEndDate);

  // Build auctioneer entries array from three slots
  const auctioneerSlots = ['One', 'Two', 'Three'];
  const auctioneers = auctioneerSlots
    .map((suffix) => ({
      name: auctioneerDetail?.[`auctioneerLcnsTxt${suffix}`],
      licenseNum: auctioneerDetail?.[`auctioneerLcnsNum${suffix}`],
      phone: auctioneerDetail?.[`auctioneerPhone${suffix}`],
    }))
    .filter(
      (entry) =>
        hasContent(entry.name) ||
        hasContent(entry.licenseNum) ||
        hasContent(entry.phone)
    );

  return (
    <div id="AuctionInfo" className="commercial-pdp__auction-section">
      <h2 className="commercial-pdp__auction-heading">Auction Information</h2>
      <div className="commercial-pdp__auction-card">
        <div className="commercial-pdp__auction-grid">

          {/* Listed By Column */}
          {hasListingAgent && (
            <div className="commercial-pdp__auction-column">
              <h3 className="commercial-pdp__auction-column-header">
                Listed By:
              </h3>
              <div className="commercial-pdp__auction-entry">
                {(hasContent(listingAgentDetail.listingAgentFirstName) || hasContent(listingAgentDetail.listingAgentLastName) || hasContent(listingAgentDetail.listingAgentName)) && (
                  <div className="commercial-pdp__auction-value">
                    {hasContent(listingAgentDetail.listingAgentFirstName) || hasContent(listingAgentDetail.listingAgentLastName)
                      ? [listingAgentDetail.listingAgentFirstName, listingAgentDetail.listingAgentLastName]
                          .filter(hasContent)
                          .map((s) => String(s).trim())
                          .join(' ')
                      : String(listingAgentDetail.listingAgentName).trim()
                    }
                  </div>
                )}
                {hasContent(listingAgentDetail.listingAgentLicenseNumber) && (
                  <div className="commercial-pdp__auction-label">
                    {"License No. " + String(listingAgentDetail.listingAgentLicenseNumber).trim()}
                  </div>
                )}
                {hasContent(listingAgentDetail.agency) && (
                  <div className="commercial-pdp__auction-label">
                    {String(listingAgentDetail.agency).trim()}
                  </div>
                )}
                
                {/* RHSS address display */}
                {isRhss && (
                  <>
                    {(hasContent(listingAgentDetail.listingAgentAddress1) || hasContent(listingAgentDetail.listingAgentAddress2)) && (
                      <div className="commercial-pdp__auction-label">
                        {[listingAgentDetail.listingAgentAddress1, listingAgentDetail.listingAgentAddress2]
                          .filter(hasContent)
                          .map((s) => String(s).trim())
                          .join(' ')}
                      </div>
                    )}
                    {(hasContent(listingAgentDetail.listingAgentCity) || hasContent(listingAgentDetail.listingAgentState) || hasContent(listingAgentDetail.listingAgentZip)) && (
                      <div className="commercial-pdp__auction-label">
                        {[listingAgentDetail.listingAgentCity, listingAgentDetail.listingAgentState]
                          .filter(hasContent)
                          .map((s) => String(s).trim())
                          .join(', ')}
                        {hasContent(listingAgentDetail.listingAgentZip) && ` ${String(listingAgentDetail.listingAgentZip).trim()}`}
                      </div>
                    )}
                  </>
                )}
                {hasContent(listingAgentDetail.listingAgentPhone) && (
                  <a
                    href={`tel:${String(listingAgentDetail.listingAgentPhone).trim()}`}
                    className="commercial-pdp__auction-phone-link"
                    aria-label={`Call ${
                      hasContent(listingAgentDetail.listingAgentFirstName) || hasContent(listingAgentDetail.listingAgentLastName)
                        ? [listingAgentDetail.listingAgentFirstName, listingAgentDetail.listingAgentLastName].filter(hasContent).map((s) => String(s).trim()).join(' ')
                        : hasContent(listingAgentDetail.listingAgentName) ? String(listingAgentDetail.listingAgentName).trim() : 'agent'
                    } at ${String(listingAgentDetail.listingAgentPhone).trim()}`}
                  >
                    <PhoneIcon />
                    {String(listingAgentDetail.listingAgentPhone).trim()}
                  </a>
                )}
                  {isRhss && (
               
                  <div className="commercial-pdp__auction-label">
                    <span className="commercial-pdp__auction-label-about">Main Location: </span>
                    Atlanta, GA
               
                </div>
              )}
              </div>
             
           
              {/* RHSS disclaimer */}
              {isRhss && (
                <div className="commercial-pdp__auction-entry commercial-pdp__auction-rhss-disclaimer">
                  
                  <div className="commercial-pdp__auction-label commercial-pdp__auction-rhss-text">
                    <span className="commercial-pdp__auction-rhss-highlight">
                      REALHome Services and Solutions, Inc.
                    </span>
                    {' '}is a licensed real estate brokerage in 49 states plus Washington D.C. and its affiliate, REALHome Services and Solutions - CT, Inc., is a licensed real estate brokerage in Connecticut.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Auction By Column */}
          {auctioneers.length > 0 && (
            <div className="commercial-pdp__auction-column">
              <h3 className="commercial-pdp__auction-column-header">
                Auction By:
              </h3>
              {auctioneers.map((entry, index) => (
                <div
                  key={index}
                  className="commercial-pdp__auction-entry"
                >
                  {hasContent(entry.name) && (
                    <div className="commercial-pdp__auction-value">
                      {String(entry.name).trim()}
                    </div>
                  )}
                  {hasContent(entry.licenseNum) && (
                    <div className="commercial-pdp__auction-label">
                      License No. {String(entry.licenseNum).trim()}
                    </div>
                  )}
                  {hasContent(entry.phone) && (
                    <a
                      href={`tel:${String(entry.phone).trim()}`}
                      className="commercial-pdp__auction-phone-link"
                      aria-label={
                        hasContent(entry.name)
                          ? `Call ${String(entry.name).trim()} at ${String(entry.phone).trim()}`
                          : `Call ${String(entry.phone).trim()}`
                      }
                    >
                      <PhoneIcon />
                      {String(entry.phone).trim()}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Auction Firm Column */}
          {hasFirmContent && (
            <div className="commercial-pdp__auction-column">
              <h3 className="commercial-pdp__auction-column-header">
                Auction Firm:
              </h3>
              <div className="commercial-pdp__auction-entry">
                {hasContent(auctionFirmDetail.auctioneerLcnsTxtOne) && (
                  <div className="commercial-pdp__auction-value">
                    {String(auctionFirmDetail.auctioneerLcnsTxtOne).trim()}
                  </div>
                )}
                {hasContent(auctionFirmDetail.auctioneerLcnsNumOne) && (
                  <div className="commercial-pdp__auction-label">
                    License No. {String(auctionFirmDetail.auctioneerLcnsNumOne).trim()}
                  </div>
                )}
                {hasContent(auctionFirmDetail.auctioneerPhoneOne) && (
                  <a
                    href={`tel:${String(auctionFirmDetail.auctioneerPhoneOne).trim()}`}
                    className="commercial-pdp__auction-phone-link"
                    aria-label={
                      hasContent(auctionFirmDetail.auctioneerLcnsTxtOne)
                        ? `Call ${String(auctionFirmDetail.auctioneerLcnsTxtOne).trim()} at ${String(auctionFirmDetail.auctioneerPhoneOne).trim()}`
                        : `Call ${String(auctionFirmDetail.auctioneerPhoneOne).trim()}`
                    }
                  >
                    <PhoneIcon />
                    {String(auctionFirmDetail.auctioneerPhoneOne).trim()}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Auction Firm Column (Static)
          <div className="commercial-pdp__auction-column">
            <h3 className="commercial-pdp__auction-column-header">
              Auction Firm:
            </h3>
            <div className="commercial-pdp__auction-entry">
              <div className="commercial-pdp__auction-value">
                Altisource Online Auction, Inc.
              </div>
              <div className="commercial-pdp__auction-label">
                License No. CA-Lic#AuctionBond GM252054
              </div>
              <a
                href="tel:(214) 555-0124"
                className="commercial-pdp__auction-phone-link"
                aria-label="Call Altisource Online Auction at (214) 555-0124"
              >
                <PhoneIcon />
                (214) 555-0124
              </a>
            </div>
          </div> */}

          {/* About the Property Column */}
          {hasPropertyContent && (
            <div className="commercial-pdp__auction-column">
              <h3 className="commercial-pdp__auction-column-header">
                About the Property:
              </h3>
              {hasContent(listingDetail?.dateFormattedListingStartDate) && (
                <div className="commercial-pdp__auction-entry-about">
                  <span className="commercial-pdp__auction-label-about">
                    Auction Begins In: 
                  </span>
                  <span className="commercial-pdp__auction-label">
                    {" " + listingDetail.dateFormattedListingStartDate}
                    {hasContent(listingDetail?.timeFormattedListingStartTime) && ` at ${listingDetail.timeFormattedListingStartTime}`}
                  </span>
                </div>
              )}
              {hasContent(listingDetail?.dateFormattedListingEndDate) && (
                <div className="commercial-pdp__auction-entry-about">
                  <span className="commercial-pdp__auction-label-about">
                    Auction Ends In: 
                  </span>
                  <span className="commercial-pdp__auction-label">
                    {" " + listingDetail.dateFormattedListingEndDate}
                    {hasContent(listingDetail?.timeFormattedListingEndTime) && ` at ${listingDetail.timeFormattedListingEndTime}`}
                  </span>
                </div>
              )}
              {hasContent(propertyDetails?.propertyCreatedDate) && (
                <div className="commercial-pdp__auction-entry-about">
                  <span className="commercial-pdp__auction-label-about">
                    Date on Market: 
                  </span>
                  <span className="commercial-pdp__auction-label">
                    {" " + formatTimestamp(propertyDetails.propertyCreatedDate)}
                  </span>
                </div>
              )}
              {hasContent(propertyDetails?.propertyLastUpdated) && (
                <div className="commercial-pdp__auction-entry-about">
                  <span className="commercial-pdp__auction-label-about">
                    Last Updated: 
                  </span>
                  <span className="commercial-pdp__auction-label">
                    {" " + formatTimestamp(propertyDetails.propertyLastUpdated)}
                  </span>
                </div>
              )}
             
                <div className="commercial-pdp__auction-entry">
                  <span className="commercial-pdp__auction-label-about">
                    Property ID: 
                  </span>
                  <span className="commercial-pdp__auction-label">
                    {" " +  propertyDetails?.propertyId}
                  </span>
                </div>
              
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuctionInformation;